import { getAudioCapabilities } from '../../audioCapabilities';
import { PlaybackTech } from '../types';
import { AudioSrcEngine } from './AudioSrcEngine';
import { IAudioEngine } from './IAudioEngine';
import { MediaSourceEngine } from './MediaSourceEngine';

/**
 * Factory for creating audio engines based on playback technology.
 *
 * Handles the creation and initialization of different engine types:
 * - MediaSourceEngine: For browsers supporting MSE API (gapless playback)
 * - AudioSrcEngine: Fallback for browsers without MSE support
 *
 * The factory encapsulates engine creation logic and tech detection, making it
 * easier to add new engine types or modify creation behavior in the future.
 */
export class AudioEngineFactory {
  /**
   * Detects which playback technology to use based on browser capabilities.
   * MediaSource API enables gapless playback but isn't supported everywhere.
   *
   * @returns The detected playback technology
   */
  static detectTech(): PlaybackTech {
    return getAudioCapabilities().mediaSourceSupported
      ? PlaybackTech.MEDIA_SOURCE
      : PlaybackTech.AUDIO_SRC;
  }

  /**
   * Creates and initializes an audio engine, auto-detecting the appropriate
   * playback technology based on browser capabilities.
   *
   * MediaSourceEngine requires async initialization for MSE setup, while
   * AudioSrcEngine can be used immediately after construction.
   *
   * @returns A promise that resolves to an initialized audio engine
   */
  static async create(): Promise<IAudioEngine> {
    const tech = this.detectTech();
    return this.createWithTech(tech);
  }

  /**
   * Creates and initializes an audio engine for the specified playback technology.
   *
   * MediaSourceEngine requires async initialization for MSE setup, while
   * AudioSrcEngine can be used immediately after construction.
   *
   * @param tech - The playback technology to use
   * @returns A promise that resolves to an initialized audio engine
   */
  static async createWithTech(tech: PlaybackTech): Promise<IAudioEngine> {
    if (tech === PlaybackTech.MEDIA_SOURCE) {
      const engine = new MediaSourceEngine();
      await engine.init();
      return engine;
    }
    return new AudioSrcEngine();
  }
}
